import {ActionSheetProvider} from '@expo/react-native-action-sheet';
import {NavigationContainer} from '@react-navigation/native';
import {Amplify, Auth, I18n} from 'aws-amplify';
import * as SplashScreen from 'expo-splash-screen';
import {checkForUpdateAsync, fetchUpdateAsync, reloadAsync} from 'expo-updates';
import React, {useEffect, useState} from 'react';
import {Alert, Platform} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {authStackLinking} from './auth';
import {Layout} from './components/Layout';
import {AuthContext, IAuthState} from './lib/authContext';
import {loadFonts} from './lib/loadFonts';
import {mainStackLinking} from './screens/MainStack';
import {config} from './stage.config';
import {NativeBaseProvider, Text} from 'native-base';
import {customTheme} from './themes/default';
import {navigationRef} from './screens/navigation';

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

Amplify.configure({
  Auth: {
    identityPoolId: config.identityPoolId,
    region: config.region,
    userPoolId: config.userPoolId,
    userPoolWebClientId: config.userPoolWebClientId,
    mandatorySignIn: true
  },
  Analytics: {
    disabled: true
  }
});

I18n.setLanguage('en');

const linking = {
  config: {
    screens: {
      Screens: mainStackLinking,
      AuthScreens: authStackLinking
    }
  }
};

export default () => {
  const [authState, setAuthState] = useState<IAuthState>({
    isAuthenticated: false,
    currentUser: null
  });
  const [isLoading, setLoading] = useState(true);

  const signOut = async () => {
    await Auth.signOut();
    setAuthState({isAuthenticated: false, currentUser: null});
  };

  const checkForUpdates = async () => {
    if (__DEV__ || Platform.OS === 'web') return;
    try {
      const update = await checkForUpdateAsync();
      if (update.isAvailable) {
        await fetchUpdateAsync();
        Alert.alert(
          'Update',
          'New version is available',
          [
            {text: 'Cancel', style: 'cancel'},
            {text: 'Reload', onPress: () => reloadAsync()}
          ],
          {cancelable: true}
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  const checkAuth = async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser();
      setAuthState({currentUser, isAuthenticated: true});
    } catch {
      setAuthState({currentUser: null, isAuthenticated: false});
    }
  };

  const loadApp = async () => {
    try {
      await Promise.all([loadFonts(), checkAuth(), checkForUpdates()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApp();
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <NativeBaseProvider theme={customTheme}>
      <AuthContext.Provider value={{...authState, setAuthState, signOut}}>
        <SafeAreaProvider
          style={
            Platform.OS === 'web'
              ? {maxWidth: 500, width: '100%', flex: 1, alignSelf: 'center'}
              : undefined
          }>
          <ActionSheetProvider>
            <NavigationContainer
              //@ts-ignore
              linking={linking}
              ref={navigationRef}
              onReady={async () => {
                await SplashScreen.hideAsync();
              }}
              documentTitle={{
                formatter: (options, route) => `${options?.title ?? route?.name} | MONEI Wallet`
              }}
              fallback={<Text>Loading…</Text>}>
              <Layout />
            </NavigationContainer>
          </ActionSheetProvider>
        </SafeAreaProvider>
      </AuthContext.Provider>
    </NativeBaseProvider>
  );
};
